import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { RequisitionDetail } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { FormActions } from '#client/components/ui/FormActions';
import { Page } from '#client/components/ui/Page';
import { PageLoading, PageMessage } from '#client/components/ui/PageStatus';
import { useFormValues } from '#client/hooks/useFormValues';
import { useResourceItem } from '#client/hooks/useResourceItem';
import { useMutation } from '#client/hooks/useMutation';
import { RESOURCE, routes } from '#client/lib/routes';
import {
  type DeliveryLine,
  buildReceiptBody,
  findDeliveryProblem,
  initialDeliveryHeader,
  initialDeliveryLines,
  updateDeliveryLine,
} from './receive-stock.logic';
import { DeliveryHeaderCard, DeliveryLinesCard } from './ReceiveStockSections';
import { recordDelivery } from './procurement.service';
import { ButtonLink } from '#client/components/ui/ButtonLink';

/**
 * Signing for a delivery.
 *
 * Pre-filled with **what is still outstanding**, not with what was ordered: on a
 * part-delivered order the number the receiving clerk is checking against is the
 * balance, and making them subtract is how a quantity gets keyed wrong.
 *
 * Every line is opt-in. A delivery of three of the five reagents on an order is
 * the ordinary case, and a form that posted all five with two of them zeroed
 * would either be refused or record deliveries that did not happen.
 */
export function ReceiveStockPage() {
  const { id } = useParams<{ id: string }>();
  const requisition = useResourceItem<RequisitionDetail>(RESOURCE.requisitions, id);

  if (requisition.loading) return <PageLoading title="Record a delivery" />;
  if (!requisition.data) {
    return (
      <PageMessage title="Reagent order not found">
        {requisition.error ?? 'It may have been cancelled while this page was open.'}
      </PageMessage>
    );
  }

  return <Form requisition={requisition.data} />;
}

function Form({ requisition }: { requisition: RequisitionDetail }) {
  const navigate = useNavigate();
  const receipt = useMutation('That delivery could not be recorded.');
  const [validationMessage, setValidationMessage] = useState<string | null>(null);

  const header = useFormValues(initialDeliveryHeader(new Date()));
  const [lines, setLines] = useState<DeliveryLine[]>(() => initialDeliveryLines(requisition.lines));

  async function handleSubmit() {
    const included = lines.filter((line) => line.include);
    const problem = findDeliveryProblem(included);
    setValidationMessage(problem);
    if (problem) return;

    const outcome = await receipt.run(() =>
      recordDelivery(requisition.id, buildReceiptBody(header.values, included)),
    );
    if (outcome.ok) void navigate(routes.requisition(requisition.id));
  }

  const errorMessage = validationMessage ?? receipt.errorMessage;

  return (
    <Page
      title={`Delivery against ${requisition.number}`}
      subtitle="What actually turned up — this is what puts stock on the shelf"
      backAction={
        <ButtonLink variant="secondary" size="md" to={routes.requisition(requisition.id)}>
          Back to the order
        </ButtonLink>
      }
      fullWidth
    >
      <form
        className="c-form-stack"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        {errorMessage && <Banner tone="critical">{errorMessage}</Banner>}

        <DeliveryHeaderCard values={header.values} onFieldChange={header.set} />

        <DeliveryLinesCard
          lines={lines}
          onChange={(lineId, changes) =>
            setLines((current) => updateDeliveryLine(current, { lineId, changes }))
          }
        />

        <FormActions
          cancelTo={routes.requisition(requisition.id)}
          submitLabel="Record the delivery"
          submitting={receipt.isPending}
        />
      </form>
    </Page>
  );
}
